import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TutorialMascot from './TutorialMascot';
import useTutorialAudio from './useTutorialAudio';
import { parseRichText, splitPages, countCharacters, revealSegments } from './richText';
import './GameDialogue.css';

const TYPING_INTERVAL_MS = 12;
// Enough clearance that the box never sits on top of what it's pointing at.
const DOCK_GAP = 18;
// Where the box sits when nothing of the app's own is in the way.
const EDGE_INSET = { top: 18, bottom: 22 };

/**
 * How far in from `edge` the box has to start to clear the app's own floating
 * chrome. The editor's toolbar is centred at the top of the canvas — exactly
 * where a top-docked bubble lands — so it has to be measured, not assumed.
 */
const insetFor = (edge) => {
  let inset = EDGE_INSET[edge];

  document.querySelectorAll(`[data-tutorial-chrome="${edge}"]`).forEach((node) => {
    const rect = node.getBoundingClientRect();
    if (!rect.height) return;

    const needed = edge === 'top'
      ? rect.bottom + DOCK_GAP
      : window.innerHeight - rect.top + DOCK_GAP;

    if (needed > inset) inset = needed;
  });

  return inset;
};

const renderSegments = (segments) =>
  segments.map((segment, index) => {
    const lines = segment.text.split('\n');
    const body = lines.map((line, lineIndex) => (
      <React.Fragment key={lineIndex}>
        {lineIndex > 0 && <br />}
        {line}
      </React.Fragment>
    ));

    return segment.bold
      ? <strong key={index}>{body}</strong>
      : <React.Fragment key={index}>{body}</React.Fragment>;
  });

/**
 * Dialogue HUD for the guided tutorial: a mascot that talks the learner
 * through each step from a speech bubble docked out of the way.
 *
 * Copy is revealed a character at a time and paused between blocks, so the
 * learner reads at the pace of the walkthrough rather than being handed a wall
 * of text. The box docks to whichever edge of the screen isn't occupied by the
 * control being highlighted.
 */
const GameDialogue = ({
  stepData,
  progress,
  targetRect,
  onNext,
  onPrevious,
  onCollapse,
  onExit,
  onComplete,
  canGoBack,
  onReadingChange,
  tutorialId,
  stepReady = true,
  holeClipPath = 'none',
}) => {
  const pages = useMemo(() => splitPages(stepData?.description), [stepData?.description]);
  const [pageIndex, setPageIndex] = useState(0);
  const [revealed, setRevealed] = useState(0);
  const boxRef = useRef(null);
  const [dock, setDock] = useState('top');
  const [insets, setInsets] = useState(EDGE_INSET);

  const segments = useMemo(
    () => parseRichText(pages[pageIndex] || ''),
    [pages, pageIndex],
  );
  const total = useMemo(() => countCharacters(segments), [segments]);

  const isTyping = revealed < total;
  const isLastPage = pageIndex >= pages.length - 1;

  // Restart the dialogue whenever the step changes.
  useEffect(() => {
    setPageIndex(0);
    setRevealed(0);
  }, [stepData?.id]);

  // The overlay follows along: it highlights whatever the current page is
  // talking about, and holds the click marker back until the reading is done.
  useEffect(() => {
    onReadingChange?.({ pageIndex, finished: !isTyping && isLastPage });
  }, [onReadingChange, pageIndex, isTyping, isLastPage]);

  const audio = useTutorialAudio({
    tutorialId,
    stepId: stepData?.id,
    pageIndex,
    active: !!stepData,
  });

  useEffect(() => {
    setRevealed(0);
  }, [pageIndex]);

  useEffect(() => {
    if (revealed >= total) return undefined;
    const timer = setTimeout(() => setRevealed((n) => n + 1), TYPING_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [revealed, total]);

  // Dock away from the highlighted control so the bubble never covers it, and
  // hold both edges clear of the app's own toolbars.
  useEffect(() => {
    const box = boxRef.current;
    const next = { top: insetFor('top'), bottom: insetFor('bottom') };
    setInsets((current) =>
      current.top === next.top && current.bottom === next.bottom ? current : next);

    if (!box || !targetRect) {
      setDock('top');
      return;
    }

    const height = box.offsetHeight || 200;
    const collidesWithTop = targetRect.top < next.top + height + DOCK_GAP;
    setDock(collidesWithTop ? 'bottom' : 'top');
  }, [targetRect, stepData?.id, pageIndex, revealed]);

  const advance = useCallback(() => {
    if (isTyping) {
      setRevealed(total);
      return;
    }
    if (!isLastPage) {
      setPageIndex((n) => n + 1);
    }
  }, [isTyping, isLastPage, total]);

  // Space and Enter read as "continue" in this kind of dialogue box.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== ' ' && event.key !== 'Enter') return;
      const tag = event.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || event.target?.isContentEditable) return;

      if (isTyping || !isLastPage) {
        event.preventDefault();
        advance();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [advance, isTyping, isLastPage]);

  if (!stepData) return null;

  const finishedReading = !isTyping && isLastPage;
  const showContinueChevron = isTyping || !isLastPage;
  const actionGated = finishedReading && !stepData.showNext && !stepData.showComplete;

  return (
    <>
      {/* While there's still copy to read, the whole screen advances the
          dialogue — that's what a learner tries first.

          The highlighted control is cut out of it. A step that asks for
          something other than a click — typing a size into a field, say — is
          asked for on a page that still has pages after it, and a catcher
          stretched over everything answers that request by eating it. */}
      {showContinueChevron && (
        <div
          className="game-dialogue-catcher"
          style={{ clipPath: holeClipPath }}
          onClick={advance}
          role="presentation"
        />
      )}

      <div
        className={`game-dialogue game-dialogue--${dock}`}
        ref={boxRef}
        style={dock === 'top' ? { top: insets.top } : { bottom: insets.bottom }}
      >
      <div className="game-dialogue-inner">
        <div className="game-dialogue-mascot">
          <TutorialMascot talking={isTyping} cheering={!!stepData.showComplete} />
        </div>

        <div
          className="game-dialogue-bubble"
          onClick={advance}
          role="presentation"
        >
          <div className="game-dialogue-nameplate">
            <span className="game-dialogue-name">{stepData.title}</span>

            {audio.available && (
              <button
                type="button"
                className="game-dialogue-audio"
                onClick={(event) => {
                  event.stopPropagation();
                  audio.toggle();
                }}
                aria-label={audio.enabled ? 'Turn off narration' : 'Turn on narration'}
                title={audio.enabled ? 'Sound on' : 'Sound off'}
              >
                {audio.enabled ? '🔊' : '🔇'}
              </button>
            )}

            <span className="game-dialogue-step">
              {progress.current}<span className="game-dialogue-step-total">/{progress.total}</span>
            </span>
          </div>

          <div className="game-dialogue-text">
            {renderSegments(revealSegments(segments, revealed))}
          </div>

          {showContinueChevron && !isTyping && (
            <div className="game-dialogue-continue">
              <span className="game-dialogue-chevron">▼</span>
              Click anywhere to continue
              {pages.length > 1 && (
                <span className="game-dialogue-pages">
                  {pageIndex + 1} of {pages.length}
                </span>
              )}
            </div>
          )}

          {actionGated && (
            <div className="game-dialogue-prompt">
              <span className="game-dialogue-prompt-dot" />
              {stepData.picker && 'Choose a design below to begin'}
              {stepData.waiting && 'Working on it — I\'ll carry on when this finishes'}
              {!stepData.picker && !stepData.waiting && (
                stepReady
                  ? 'Your turn — click the highlighted control'
                  : 'Waiting for the app to catch up — one moment'
              )}
            </div>
          )}

          <div className="game-dialogue-actions" onClick={(e) => e.stopPropagation()}>
            {canGoBack && (
              <button type="button" className="game-btn game-btn--ghost" onClick={onPrevious}>
                ◀ Back
              </button>
            )}

            <div className="game-dialogue-actions-right">
              {/* Skipping the picker would leave the walkthrough with no design
                  to work on, so that one step has no escape hatch. */}
              {actionGated && !stepData.picker && !stepData.waiting && (
                <button type="button" className="game-btn game-btn--ghost" onClick={onNext}>
                  Skip step ▶
                </button>
              )}

              {finishedReading && stepData.showNext && (
                <button type="button" className="game-btn game-btn--primary" onClick={onNext}>
                  {stepData.nextText || 'Next'} ▶
                </button>
              )}

              {finishedReading && stepData.showComplete && (
                <button type="button" className="game-btn game-btn--primary" onClick={onComplete}>
                  Finish ★
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Both ways out, on every step. Getting stuck in a walkthrough is
            worse than abandoning one, so neither is ever withheld — not even on
            the steps that gate on an action. */}
        <div className="game-dialogue-corner">
          {/* Hides the walkthrough rather than ending it: the learner keeps
              their place, and a tap on the tab that's left brings it back. */}
          <button
            type="button"
            className="game-dialogue-close"
            onClick={onCollapse}
            aria-label="Minimise tutorial"
            title="Minimise — your place is kept"
          >
            –
          </button>

          <button
            type="button"
            className="game-dialogue-close game-dialogue-close--exit"
            onClick={onExit}
            aria-label="Exit tutorial"
            title="Exit the tutorial"
          >
            ✕
          </button>
        </div>
      </div>

      <div className="game-dialogue-progress">
        <div
          className="game-dialogue-progress-fill"
          style={{ width: `${progress.percentage}%` }}
        />
      </div>
      </div>
    </>
  );
};

export default GameDialogue;
